import React, {useEffect, useMemo, useRef, useState} from 'react'
import {SearchInput} from '../components'
import type {StoreItem, StoreWeekStatusItem} from './StoreContext'
import {RoleTag, StoreStatusTag, storeLabel} from './StoreTags'
import styles from './StoreList.module.scss'

type StoreListProps = {
  stores: Array<StoreItem>
  activeStoreId: number | undefined
  onSelect: (storeId: number) => void
  /**
   * Show a search field above the list from this many stores upwards.
   * A controller can be a member of 200+ stores, so the list must be filterable.
   */
  searchThreshold?: number
  /** Focus the search field on mount (when it's shown) — handy inside a popover. */
  autoFocusSearch?: boolean
  /** Current-week status by storeId — StoreStatusTag renders nothing until it arrives. */
  storeStatus?: Record<number, StoreWeekStatusItem>
}

/**
 * The searchable list of stores, shared by the desktop dropdown (StoreSwitcher)
 * and the mobile burger menu (AppShell). Filters by name or code; selecting a
 * store calls onSelect (the shell gates the actual switch behind a dirty check).
 */
export const StoreList = ({
  stores,
  activeStoreId,
  onSelect,
  searchThreshold = 8,
  autoFocusSearch = false,
  storeStatus = {},
}: StoreListProps): React.ReactElement => {
  const [search, setSearch] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const showSearch = stores.length >= searchThreshold

  useEffect(() => {
    if (autoFocusSearch && showSearch) searchRef.current?.focus()
  }, [autoFocusSearch, showSearch])

  const term = search.trim().toLowerCase()
  const filtered = useMemo(() => {
    if (term === '') return stores
    return stores.filter(
      (store) =>
        store.name.toLowerCase().includes(term) ||
        (store.code !== undefined && store.code.toLowerCase().includes(term)),
    )
  }, [stores, term])

  return (
    <div className={styles['store-list']}>
      {showSearch && (
        // Own wrapper rather than passing the class to SearchInput: the sticky
        // row and the search field both need `position`, and this class would
        // land on the same element as the `modul` the field carries — where the
        // sticky, being in a module sheet, would quietly win.
        <div className={`p-t-4 p-h-4 p-b-8 bg-white ${styles['store-list__search']}`}>
          <SearchInput
            inputRef={searchRef}
            placeholder="Buscar tienda…"
            ariaLabel="Buscar tienda"
            value={search}
            onChange={setSearch}
          />
        </div>
      )}

      {filtered.length === 0 ? (
        <p className={`m-0 txt-light-kale ${styles['store-list__empty']}`}>No hay tiendas que coincidan.</p>
      ) : (
        <ul className={`m-0 p-0 ${styles['store-list__options']}`} role="listbox">
          {filtered.map((store) => {
            const isActive = store.id === activeStoreId
            return (
              // !TODO: DELETE THIS <button> and move its class, click handler and
              // keyboard handling onto the <li> because ARIA forbids interactive
              // descendants inside `role="option"` — the option itself has to be
              // the clickable element. Belongs to the accessibility pass, which is
              // out of scope for this programme (see PLAN.md, "Fuera de alcance");
              // doing it here would change the markup and the accessibility tree.
              <li key={store.id} role="option" aria-selected={isActive}>
                <button
                  type="button"
                  className={[
                    'row-flx',
                    'flx-a-c',
                    'flx-j-b',
                    'flx-gap-16',
                    'txt-a-l',
                    'border-xs',
                    'full-width',
                    styles['store-list__option'],
                    isActive ? `bg-light-olive ${styles['store-list__option--active']}` : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => onSelect(store.id)}
                >
                  <span className={`txt-bold-500 txt-kale ${styles['store-list__option-name']}`}>{storeLabel(store)}</span>
                  <span className="row-flx flx-a-c flx-gap-8 itm-flx-s-0">
                    <StoreStatusTag status={storeStatus[store.id]} />
                    <RoleTag role={store.role} />
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
